import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, CalendarDays, MapPin, ExternalLink } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { formatDate as fmtDate } from "../lib/format.js";
import { BonusoveVstupyBlok, PilulkaBonus } from "./BonusoveVstupy.jsx";

// Panel "Bootcamp" v detailu klienta.
// Data z /api/bootcampy?klientId= (samostatné tabulky bootcamp_*, NENÍ v clients),
// takže bootcamp neovlivňuje tréninky, balíček ani MRR klientky. Panel se schová,
// když klientka na žádném běhu není.

function notionIdFromUrl(u) {
  const m = String(u || "").match(/([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return m ? m[1] : "";
}
function den(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : fmtDate(d);
}
function Delic() {
  return <span aria-hidden className="w-px h-3 bg-current opacity-40" />;
}

export default function ClientBootcampSection({ klientId, klientNotion, bezRamce = false, onCount }) {
  const id = klientId || notionIdFromUrl(klientNotion);
  const uzka = useUzkaObrazovka();
  const [ucasti, setUcasti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smiBonus, setSmiBonus] = useState(false);
  const [smiSmazatBonus, setSmiSmazatBonus] = useState(false);
  // Po přidání nebo smazání bonusu se data načtou znovu (1. 11. 2026).
  const [verze, setVerze] = useState(0);

  useEffect(() => {
    let zruseno = false;
    if (!id) { setLoading(false); return undefined; }
    (async () => {
      try {
        const r = await apiFetch(`/api/bootcampy?klientId=${encodeURIComponent(id)}`);
        const j = await r.json();
        if (!zruseno) {
          setUcasti(Array.isArray(j.ucasti) ? j.ucasti : []);
          setSmiBonus(j.smiBonus === true);
          setSmiSmazatBonus(j.smiSmazatBonus === true);
        }
      } catch (_) {
        if (!zruseno) setUcasti([]);
      } finally {
        if (!zruseno) setLoading(false);
      }
    })();
    return () => { zruseno = true; };
  }, [id, verze]);

  // Odznak v hlavičce sekce; null znamená "sekci vůbec nezobrazuj" (9.9.2026).
  useEffect(() => {
    if (typeof onCount !== "function" || loading) return;
    onCount(ucasti.length ? `${ucasti.length} ${ucasti.length === 1 ? "běh" : ucasti.length < 5 ? "běhy" : "běhů"}` : null);
  }, [ucasti, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || ucasti.length === 0) return null;

  return (
    <div className={bezRamce ? "" : "bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-sm"}>
      {!bezRamce && (
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 rounded-xl bg-orange-50 text-orange-600"><Flame className="w-4 h-4" /></div>
          <h3 className="text-base font-bold text-slate-900">Bootcamp</h3>
        </div>
      )}

      <div className="space-y-3">
        {ucasti.map((u) => (
          <div key={u.id} className="border border-slate-200 rounded-xl p-3">
            {/* 9.9.2026 — telefon: název má vlastní řádek, odkaz je tlačítko 44 px,
                datum, místo a trenér jsou odznaky, vstupy a platba dvě dlaždice. */}
            {uzka && (
              <>
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 text-[15px] font-bold leading-tight text-slate-900">{u.nazev}</span>
                  <Link to={`/bootcampy?beh=${u.bootcamp_id}`} title="Otevřít běh" aria-label="Otevřít běh"
                    className="flex h-11 w-11 flex-none items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:text-slate-700">
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-bold text-slate-600">{den(u.datum_od)} – {den(u.datum_do)}</span>
                  {u.misto && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-bold text-slate-600">{u.misto}</span>}
                  {u.trener_name && <span className="rounded-full bg-plum-50 px-2 py-0.5 text-[11.5px] font-bold text-plum-700">{u.trener_name}</span>}
                  <PilulkaBonus pocet={u.bonus_vstupu} />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  <div className="rounded-xl bg-emerald-50 py-2 text-center">
                    <div className="text-[19px] font-extrabold leading-none tabular-nums text-emerald-700">{u.permice_celkem == null ? "∞" : u.zbyva}</div>
                    <div className="mt-1 text-[11px] font-semibold text-emerald-700">{u.permice_celkem == null ? "bez omezení" : `zbývá z ${u.permice_celkem}`}</div>
                  </div>
                  <div className={`flex flex-col items-center justify-center rounded-xl py-2 text-center ${u.stav === "zaplaceno" ? "bg-slate-50" : "bg-amber-50"}`}>
                    <div className={`text-[13px] font-extrabold leading-none ${u.stav === "zaplaceno" ? "text-slate-800" : "text-amber-700"}`}>{u.stav === "zaplaceno" ? "Zaplaceno" : "Nezaplaceno"}</div>
                    <div className={`mt-1 text-[11px] font-semibold ${u.stav === "zaplaceno" ? "text-slate-500" : "text-amber-700"}`}>{u.tarif || "bez tarifu"}</div>
                  </div>
                </div>
                {u.poznamka_trener && (
                  <div className="mt-2 rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-[14px] leading-snug text-slate-800">{u.poznamka_trener}</div>
                )}
              </>
            )}
            <div className={uzka ? "hidden" : "flex items-start justify-between gap-3"}>
              <div className="min-w-0">
                <div className="font-medium text-slate-900">{u.nazev}</div>
                <div className="mt-1 flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{den(u.datum_od)} až {den(u.datum_do)}</span>
                  {u.misto && <><Delic /><span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{u.misto}</span></>}
                  {u.trener_name && <><Delic /><span>{u.trener_name}</span></>}
                </div>
              </div>
              <Link to={`/bootcampy?beh=${u.bootcamp_id}`} className="text-slate-400 hover:text-slate-700 shrink-0" title="Otevřít běh">
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>

            <div className={`${uzka ? "hidden" : "flex"} mt-2.5 items-center gap-2 flex-wrap text-sm`}>
              {u.tarif && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{u.tarif}</span>}
              <PilulkaBonus pocet={u.bonus_vstupu} />
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                u.stav === "zaplaceno" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {u.stav === "zaplaceno" ? "Zaplaceno" : "Přihlášena, zatím nezaplaceno"}
              </span>
              <span className="text-slate-600 tabular-nums">
                {u.permice_celkem == null
                  ? "vstupy bez omezení"
                  : <>zbývá <b>{u.zbyva}</b> z {u.permice_celkem} vstupů</>}
              </span>
            </div>

            {u.poznamka_trener && !uzka && (
              <div className="mt-2 text-sm text-slate-600 bg-slate-50 rounded-xl px-3 py-2">{u.poznamka_trener}</div>
            )}

            {/* Bonusové vstupy (1. 11. 2026, hlášení 191). */}
            {u.stav !== "zrusena" && (
              <BonusoveVstupyBlok
                className="mt-3"
                cil={u.bonus_cil}
                bonusy={u.bonusy || []}
                smiBonus={smiBonus}
                smiSmazat={smiSmazatBonus}
                onZmena={() => setVerze((v) => v + 1)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
